import { navigation } from "./navigation-items";
import { useLocation } from "react-router-dom";
import LogoutButton from "@/components/authentication/LogoutButton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Services
import { useOrganization } from "@/services/organization/getOrganizationDetails"

// Hooks
import { useTenantNavigation } from "@/hooks/navigation/useTenantNavigation";

// Shadcn components
import {
  Sidebar,
} from "@/components/ui/sidebar"

// Icons
import { AlertTriangle } from "lucide-react";

export function AppSidebar() {
  // Store current organization
  const { organization, loading, error } = useOrganization();
  const { getTenantPath } = useTenantNavigation();
  const location = useLocation();

  const isPathActive = (path: string) => {
    const currentPath = location.pathname;
    const tenantPath = getTenantPath(path);
    
    // Special case for dashboard - consider all event-related paths as part of dashboard
    if (path === '/dashboard') {
      return currentPath === tenantPath || 
             currentPath.includes('/events/') || 
             currentPath.includes('/event/');
    }
    
    return currentPath === tenantPath;
  };

  return (
    <Sidebar className="hidden lg:block h-screen">
      <div className="flex h-full flex-col px-3 py-4 pb-0 justify-between">
        {/* Top section */}
        <div className="flex flex-col">
          <div className="flex flex-col gap-2 mb-7 mt-3 ">
            {/* Organization and User section */}
            <div className="flex items-center gap-3">
              <div className="flex flex-shrink-0 items-center justify-center w-8 h-8 rounded-full bg-primary text-white text-sm font-bold shadow-sm">
                {organization?.name ? organization.name.charAt(0) : "O"}
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <h2 className="text-lg font-semibold truncate whitespace-nowrap overflow-hidden flex-1">
                      {loading ? "Loading..." : error ? "Error" : organization?.name || "Organization"}
                    </h2>
                  </TooltipTrigger>
                  <TooltipContent>
                    {organization?.name || "Organization"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          <nav className="flex-1 space-y-3">
            {navigation.map((item) => {
              const isActive = isPathActive(item.href);
              return (
                <a
                  key={item.name}
                  href={getTenantPath(item.href)}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 text-md transition-colors ${
                    isActive 
                      ? "bg-primary text-primary-foreground" 
                      : "hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.name}</span>
                </a>
              );
            })}
          </nav>
        </div>

        {/* Bottom section */}
        <div className="flex flex-col space-y-3">
          <a
            href="mailto:support@tricode.cloud?subject=%5BBug%20Report%5D%20Issue%20with%20TriEvents%20Platform"
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-md text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <AlertTriangle className="h-4 w-4" />
            <span>Report an Issue</span>
          </a>

          <LogoutButton />

          <div className="flex pt-4 pb-6 justify-center items-center">
            <img
              src="/branding/TriEvents_Logo.png"
              alt="TriEvents"
              className="h-6"
            />
          </div>
        </div>
      </div>
    </Sidebar>
  )
}